import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  decimal,
  varchar,
  jsonb,
  index,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

/**
 * Agency Twilio Settings
 *
 * Stores Twilio sub-account configuration per agency.
 * Similar to agencyEmailSettings for SendGrid subusers.
 */
export const agencyTwilioSettings = pgTable("agency_twilio_settings", {
  id: serial("id").primaryKey(),
  agencyId: varchar("agency_id", { length: 255 }).notNull().unique(),

  // Twilio Sub-Account
  twilioSubaccountSid: varchar("twilio_subaccount_sid", { length: 50 }),
  twilioSubaccountAuthToken: text("twilio_subaccount_auth_token"), // Encrypted
  twilioSubaccountFriendlyName: varchar("twilio_subaccount_friendly_name", { length: 100 }),

  // Provisioning Status
  provisioningStatus: varchar("provisioning_status", { length: 50 }).default("pending"), // pending, provisioning, provisioned, failed
  provisioningError: text("provisioning_error"),
  isActive: boolean("is_active").default(false),

  // Usage Tracking
  smsSentThisMonth: integer("sms_sent_this_month").default(0),
  lastUsageSync: timestamp("last_usage_sync"),

  // Audit
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => ({
  agencyIdIdx: index("agency_twilio_settings_agency_id_idx").on(table.agencyId),
}));

export const insertAgencyTwilioSettingsSchema = createInsertSchema(agencyTwilioSettings)
  .omit({ id: true, createdAt: true, updatedAt: true });

export type AgencyTwilioSettings = typeof agencyTwilioSettings.$inferSelect;
export type InsertAgencyTwilioSettings = z.infer<typeof insertAgencyTwilioSettingsSchema>;

/**
 * Phone Number Capabilities
 */
export const phoneCapabilitiesSchema = z.object({
  voice: z.boolean().default(false),
  sms: z.boolean().default(false),
  mms: z.boolean().default(false),
});

export type PhoneCapabilities = z.infer<typeof phoneCapabilitiesSchema>;

/**
 * Agency Phone Numbers
 *
 * Stores phone numbers purchased by agencies through Twilio.
 */
export const agencyPhoneNumbers = pgTable("agency_phone_numbers", {
  id: serial("id").primaryKey(),
  agencyId: varchar("agency_id", { length: 255 }).notNull(),

  // Twilio Details
  twilioPhoneNumberSid: varchar("twilio_phone_number_sid", { length: 50 }).notNull(),
  phoneNumber: varchar("phone_number", { length: 20 }).notNull(), // E.164 format
  friendlyName: varchar("friendly_name", { length: 100 }),

  // Number Type & Capabilities
  numberType: varchar("number_type", { length: 20 }), // 'local' | 'toll-free'
  capabilities: jsonb("capabilities").$type<PhoneCapabilities>(), // { voice: true, sms: true, mms: false }

  // Configuration
  isDefault: boolean("is_default").default(false),
  smsEnabled: boolean("sms_enabled").default(false), // Manual enable for SMS Marketing
  assignedToUserId: varchar("assigned_to_user_id", { length: 255 }),

  // Status
  status: varchar("status", { length: 20 }).default("active"), // active, pending, suspended
  monthlyPrice: decimal("monthly_price", { precision: 10, scale: 2 }),

  // Audit
  purchasedAt: timestamp("purchased_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => ({
  agencyIdIdx: index("agency_phone_numbers_agency_id_idx").on(table.agencyId),
  phoneNumberIdx: index("agency_phone_numbers_phone_number_idx").on(table.phoneNumber),
  smsEnabledIdx: index("agency_phone_numbers_sms_enabled_idx").on(table.smsEnabled),
}));

export const insertAgencyPhoneNumberSchema = createInsertSchema(agencyPhoneNumbers)
  .omit({ id: true, createdAt: true, updatedAt: true, purchasedAt: true })
  .extend({
    phoneNumber: z.string().regex(/^\+[1-9]\d{1,14}$/, "Phone number must be in E.164 format (+1234567890)"),
    capabilities: phoneCapabilitiesSchema.optional(),
    numberType: z.enum(["local", "toll-free"]).optional(),
    status: z.enum(["active", "pending", "suspended"]).optional(),
  });

export type AgencyPhoneNumber = typeof agencyPhoneNumbers.$inferSelect;
export type InsertAgencyPhoneNumber = z.infer<typeof insertAgencyPhoneNumberSchema>;

/**
 * Schema for updating phone number configuration
 */
export const updatePhoneNumberConfigSchema = z.object({
  friendlyName: z.string().max(100).optional(),
  smsEnabled: z.boolean().optional(),
  assignedToUserId: z.string().optional().nullable(),
  isDefault: z.boolean().optional(),
});

export type UpdatePhoneNumberConfig = z.infer<typeof updatePhoneNumberConfigSchema>;
